import {
  Badge,
  Button,
  Card,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import type { LeaveRequestStatus } from "@/modules/hr/domain/contracts";
import type { LineManagerLeaveRequestRow } from "@/modules/hr/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";

const STATUS_LABELS: Record<string, string> = {
  pending_line: "ລໍຖ້າຫົວໜ້າສາຍ",
  pending_hr: "ລໍຖ້າ HR",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

const STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending_line: "secondary",
  pending_hr: "outline",
  approved: "default",
  rejected: "destructive",
};

type Props = {
  data: LineManagerLeaveRequestRow[];
  isLoading?: boolean;
  totalCount: number;
  offset: number;
  limit: number;
  onPageChange?: (offset: number, limit: number) => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: LeaveRequestStatus | undefined) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (id: string, desc: boolean) => void;
};

export function LineManagerLeaveRequestsTable({
  data,
  isLoading,
  totalCount,
  offset,
  limit,
  onPageChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: Props) {
  const navigate = useNavigate({ from: "/app/hr/leave/line-manager" });

  const columns: TanstackReactTable.ColumnDef<LineManagerLeaveRequestRow>[] = [
    createSortableColumn<LineManagerLeaveRequestRow>(
      "employeeName",
      "ພະນັກງານ",
      {
        size: 160,
        cell: ({ row }) => (
          <span className="font-medium">{row.original.employeeName}</span>
        ),
      },
    ),
    createSortableColumn<LineManagerLeaveRequestRow>(
      "leaveTypeName",
      "ປະເພດລາ",
      {
        size: 140,
        cell: ({ row }) => row.original.leaveTypeName,
      },
    ),
    createSortableColumn<LineManagerLeaveRequestRow>("startDate", "ຊ່ວງວັນທີ", {
      size: 200,
      cell: ({ row }) =>
        `${formatDateLocal(row.original.startDate)} – ${formatDateLocal(row.original.endDate)}`,
    }),
    createSortableColumn<LineManagerLeaveRequestRow>("days", "ວັນ", {
      size: 72,
      cell: ({ row }) => `${row.original.days} ວັນ`,
    }),
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: "ສົ່ງຂໍ",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm tabular-nums">
          {formatDateLocal(row.original.createdAt)}
        </span>
      ),
      size: 130,
    },
    createSortableColumn<LineManagerLeaveRequestRow>("status", "ສະຖານະ", {
      size: 140,
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANTS[row.original.status] ?? "outline"}>
          {STATUS_LABELS[row.original.status] ?? row.original.status}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() =>
            navigate({
              to: "/app/hr/leave/line-manager/$id",
              params: { id: row.original.id },
            })
          }
          aria-label="ເບິ່ງລາຍລະອຽດ"
        >
          <Eye className="size-4" />
        </Button>
      ),
      size: 56,
    },
  ];

  const filterChoices: Array<{
    key: LeaveRequestStatus | "all";
    label: string;
  }> = [
    { key: "all", label: "ທັງໝົດທີມ" },
    { key: "pending_line", label: STATUS_LABELS.pending_line },
    { key: "pending_hr", label: STATUS_LABELS.pending_hr },
    { key: "approved", label: STATUS_LABELS.approved },
    { key: "rejected", label: STATUS_LABELS.rejected },
  ];

  return (
    <Card className="gap-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 pb-4">
        <h3 className="font-semibold">ຄຳຂໍລາຂອງທີມ</h3>
        {onStatusFilterChange && (
          <div className="flex flex-wrap gap-1">
            {filterChoices.map(({ key, label }) => (
              <Button
                key={key}
                type="button"
                variant={
                  (key === "all" && statusFilter === "all") ||
                  (key !== "all" && statusFilter === key)
                    ? "default"
                    : "secondary"
                }
                size="sm"
                className={
                  (key === "all" && statusFilter === "all") ||
                  (key !== "all" && statusFilter === key)
                    ? "bg-black text-white hover:bg-black/90"
                    : ""
                }
                onClick={() =>
                  onStatusFilterChange(
                    key === "all" ? undefined : (key as LeaveRequestStatus),
                  )
                }
              >
                {label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <DataTable<LineManagerLeaveRequestRow, unknown>
        noDataMessage="ບໍ່ມີຄຳຂໍລາໃນລາຍການນີ້"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={
          onPageChange ? (p) => onPageChange(p.offset, p.limit) : undefined
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (!onSortingChange) return;
          if (!sorting || sorting.length === 0) return;
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
        }}
      />
    </Card>
  );
}
